import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** One band of a flag, in reading order. */
export interface FlagBand {
  color: string;
  /** Relative thickness. A band with weight 2 is twice as thick as a plain one. */
  weight?: number;
}

/** How a country's flag is drawn at chip size. */
export interface FlagDesign {
  /** Band direction: top-to-bottom, or left-to-right. */
  axis: 'horizontal' | 'vertical';
  bands: FlagBand[];
  /** A white cross laid over the bands (Switzerland). */
  cross?: boolean;
  /** A solid block over the top-left corner (the United States' canton). */
  canton?: string;
  /** Centred coat of arms. Present or absent is the whole point — see EMBLEM. */
  emblem?: boolean;
}

/**
 * Flags at chip size, as CSS.
 *
 * The coat of arms is drawn as a suggestion, not as itself — at this size no
 * emblem is legible in detail. It is here because WITHOUT it Ecuador and
 * Colombia are the same flag, band for band, and an Ecuadorian reading their
 * own national-day greeting sees Colombia's. Presence is the whole message: a
 * crest in the middle or nothing there.
 */
export const FLAG_DESIGNS: Record<string, FlagDesign> = {
  ecuador: {
    axis: 'horizontal',
    bands: [{ color: '#FCD116', weight: 2 }, { color: '#003893' }, { color: '#CE1126' }],
    emblem: true,
  },
  colombia: {
    axis: 'horizontal',
    bands: [{ color: '#FCD116', weight: 2 }, { color: '#0033A0' }, { color: '#C8102E' }],
  },
  peru: {
    axis: 'vertical',
    bands: [{ color: '#D91023' }, { color: '#FFFFFF' }, { color: '#D91023' }],
  },
  uruguay: {
    axis: 'horizontal',
    bands: [
      { color: '#FFFFFF' },
      { color: '#0038A8' },
      { color: '#FFFFFF' },
      { color: '#0038A8' },
      { color: '#FFFFFF' },
    ],
  },
  argentina: {
    axis: 'horizontal',
    bands: [{ color: '#74ACDF' }, { color: '#FFFFFF' }, { color: '#74ACDF' }],
  },
  // No crest: Spain's sits toward the hoist, not centred, and its bands are
  // already unique here — nothing else in this set is red-yellow-red. The crest
  // is only load-bearing where two flags would otherwise be identical.
  spain: {
    axis: 'horizontal',
    bands: [{ color: '#AA151B' }, { color: '#F1BF00', weight: 2 }, { color: '#AA151B' }],
  },
  germany: {
    axis: 'horizontal',
    bands: [{ color: '#000000' }, { color: '#DD0000' }, { color: '#FFCE00' }],
  },
  switzerland: {
    axis: 'horizontal',
    bands: [{ color: '#DA291C' }],
    cross: true,
  },
  united_states: {
    axis: 'horizontal',
    bands: [
      { color: '#B22234' },
      { color: '#FFFFFF' },
      { color: '#B22234' },
      { color: '#FFFFFF' },
      { color: '#B22234' },
    ],
    canton: '#3C3B6E',
  },
};

/**
 * A country's flag as a small engraved chip.
 *
 * Replaces the flag emoji, which never worked where most of this app is used:
 * Windows ships no glyphs for regional-indicator pairs, so Segoe UI Emoji draws
 * them as their two letters — "EC" instead of Ecuador's flag. Drawing the bands
 * ourselves is the only way the same thing appears on every machine.
 *
 * Decorative: the greeting beside it already names the country, so the chip is
 * hidden from screen readers rather than given a redundant label.
 */
@Component({
  selector: 'primecontrol-flag-chip',
  templateUrl: './flag-chip.html',
  styleUrl: './flag-chip.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlagChip {
  /** Country id, as used by the national greetings (e.g. 'ecuador'). */
  readonly country = input<string | null>(null);

  protected readonly design = computed<FlagDesign | null>(() => {
    const country = this.country();
    return country ? (FLAG_DESIGNS[country] ?? null) : null;
  });
}
